import { useQuery } from "urql"

import type {
  GetProductWarehouseListQueryVariables,
  ProductGetFailure,
  ProductGetSuccess,
  ProductWarehouseArea,
} from "@/graphql/codegen/graphql"
import { GetProductWarehouseListDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseGetProductWarehousesProps {
  productId: number
  version?: number
  includeInactive?: boolean
}

interface UseGetProductWarehousesReturn {
  warehouseAreas: ProductWarehouseArea[]
  packageUsUomName?: string | null
  isFetching: boolean
  refetch: (variables: GetProductWarehouseListQueryVariables) => void
}

export const useGetProductWarehouses = ({
  productId,
  version,
  includeInactive,
}: UseGetProductWarehousesProps): UseGetProductWarehousesReturn => {
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const [{ data, fetching, error }, refetch] = useQuery({
    query: GetProductWarehouseListDocument,
    variables: { productId, version, includeInactive },
  })
  const output = data?.product.get
  const hasError = error || output?.__typename === "ProductGetFailure"

  if (hasError) {
    notify({
      type: NotificationType.Error,
      message: error?.message ?? (output as ProductGetFailure).error.message,
    })
  }

  if (!data || hasError) {
    return { warehouseAreas: [], packageUsUomName: null, isFetching: fetching, refetch }
  }

  const product = (output as ProductGetSuccess).product

  return {
    warehouseAreas: product.warehouseAreas ?? [],
    packageUsUomName: product.packageUsWeightUomDetails?.name,
    isFetching: fetching,
    refetch,
  }
}
